import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { cn } from "@nexus/ui";
import type { LlmUsageFeatureTotals } from "@/features/llm-usage/api/llm-usage.api";
import {
  AdminDataPanel,
  AdminDataTable,
  type AdminColumn,
} from "@/features/admin/components/layout";
import type { SortValue } from "@/features/admin/lib/table-sort";
import { translateLlmUsageFeature } from "../lib/llm-usage-feature-labels";
import { isLongTail } from "../lib/table-filters";
import { LongTailChip } from "./LongTailChip";
import {
  tokenMeasureColumns,
  tokenMeasureSortValue,
  type TokenMeasureKey,
} from "./token-measure-columns";

interface LlmUsageFeatureTableProps {
  rows: LlmUsageFeatureTotals[];
  isLoading: boolean;
  /** Rows belong to the previous filter while the next one is in flight. */
  isStale: boolean;
  /** Window total, so each row can show its share of the bill. */
  totalTokens: number;
  /** `tokenUsageLongTailPercent`: the share a row needs to escape the fold. */
  longTailPercent: number;
}

export type FeatureSortKey = TokenMeasureKey | "feature";

/** Spend, matching the order the server already ranked the rows in. */
const DEFAULT_SORT = { key: "totalTokens", dir: "desc" } as const;

/** The label an admin reads, not the kebab-case key underneath it. */
function featureLabel(row: LlmUsageFeatureTotals, t: TFunction): string {
  return row.feature == null
    ? t("admin.llmUsage.featureTable.unattributed", "Unattributed")
    : translateLlmUsageFeature(t, row.feature);
}

/**
 * Which product surfaces the tokens went to.
 *
 * Reads as the tenant table's counterpart: filter the page to one tenant and
 * this answers what that tenant was doing, while the model table answers what
 * it was doing it with.
 */
export function LlmUsageFeatureTable({
  rows,
  isLoading,
  isStale,
  totalTokens,
  longTailPercent,
}: LlmUsageFeatureTableProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [hideLongTail, setHideLongTail] = useState(false);

  const longTailCount = useMemo(
    () => rows.filter((row) => isLongTail(row.totalTokens, totalTokens, longTailPercent)).length,
    [rows, totalTokens, longTailPercent],
  );
  const visibleRows = useMemo(
    () =>
      hideLongTail
        ? rows.filter((row) => !isLongTail(row.totalTokens, totalTokens, longTailPercent))
        : rows,
    [rows, hideLongTail, totalTokens, longTailPercent],
  );

  const columns: AdminColumn<LlmUsageFeatureTotals, FeatureSortKey>[] = [
    {
      id: "feature",
      header: t("admin.llmUsage.featureTable.feature", "Feature"),
      isFill: true,
      isSortable: true,
      cell: (row) => (
        <span className={cn(row.feature == null && "text-muted-foreground")}>
          {featureLabel(row, t)}
        </span>
      ),
    },
    ...tokenMeasureColumns<LlmUsageFeatureTotals>({
      t,
      locale,
      totalTokens,
      unpricedTokensOf: (row) => row.unpricedTokens,
    }),
  ];

  const sortValue = (row: LlmUsageFeatureTotals, key: FeatureSortKey): SortValue =>
    key === "feature" ? featureLabel(row, t) : tokenMeasureSortValue(row, key);

  return (
    <AdminDataPanel
      title={t("admin.llmUsage.featureTable.title", "Features")}
      maxRows={8}
      controls={
        <LongTailChip
          active={hideLongTail}
          onToggle={() => setHideLongTail((prev) => !prev)}
          hiddenCount={longTailCount}
          thresholdPercent={longTailPercent}
        />
      }
    >
      <AdminDataTable
        caption={t("admin.llmUsage.featureTable.caption", "Token usage per feature")}
        columns={columns}
        rows={visibleRows}
        getRowId={(row) => row.feature ?? "__unattributed__"}
        isLoading={isLoading}
        isStale={isStale}
        isFiltered={visibleRows.length < rows.length}
        emptyMessage={t("admin.llmUsage.table.empty", "No LLM calls in this window")}
        noMatchMessage={t(
          "admin.llmUsage.featureTable.noMatches",
          "No features match these filters",
        )}
        defaultSort={DEFAULT_SORT}
        sortValue={sortValue}
      />
    </AdminDataPanel>
  );
}
